// shop-frontend/src/pages/ShopCartWatchers.jsx
// Opted-in buyers who left this shop's item in their cart past the threshold.
// The shop can open a DM (existing messaging) pre-filled with the items.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getShopCartWatchers, toAbsUrl, apiPut } from "../utils/api";
import { startConversation, sendMessage } from "../utils/messagesApi";

// User app origin (buyer feed lives there). Override with REACT_APP_USER_URL.
const USER_APP = process.env.REACT_APP_USER_URL || "https://moondala.one";

function money(n, currency = "USD") {
  const x = Number(n);
  return Number.isFinite(x) ? `${currency} ${x.toFixed(2)}` : "";
}

export default function ShopCartWatchers() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [thresholdDays, setThresholdDays] = useState(5);
  const [watchers, setWatchers] = useState([]);
  const [messagingId, setMessagingId] = useState("");
  const [savingThreshold, setSavingThreshold] = useState(false);

  async function saveThreshold(days) {
    const n = Math.max(1, Math.min(60, Number(days) || 5));
    setThresholdDays(n);
    setSavingThreshold(true);
    try {
      await apiPut("/shop-profile/me", { cartOutreachDays: n });
      await load();
    } catch (e) {
      setError(e?.message || "Failed to save threshold");
    } finally {
      setSavingThreshold(false);
    }
  }

  async function load() {
    setLoading(true);
    setError("");
    try {
      const data = await getShopCartWatchers();
      setWatchers(Array.isArray(data?.watchers) ? data.watchers : []);
      setThresholdDays(Number(data?.thresholdDays || 5));
    } catch (e) {
      setError(e?.message || "Failed to load cart leads");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function messageBuyer(w) {
    setMessagingId(w.userId);
    setError("");
    try {
      const conv = await startConversation({ participantType: "user", participantId: w.userId });
      const conversationId =
        conv?.conversationId || conv?._id || conv?.id || conv?.conversation?._id || conv?.conversation?.id;

      // Pre-fill the first message with the items they left behind.
      const lines = (w.items || [])
        .map((it) => `• ${it.title}${it.qty > 1 ? ` ×${it.qty}` : ""}${it.price ? ` — ${money(it.price, it.currency)}` : ""}`)
        .join("\n");
      const text =
        `Hi ${w.name}! 👋 You left these items in your cart:\n${lines}\n\n` +
        `They're still available — happy to help if you have any questions or want to complete your order.`;

      if (conversationId) {
        try {
          await sendMessage(conversationId, { text });
        } catch (e) {
          setError(e?.message || "The chat opened, but the message didn't send.");
        }
      }
      // Open the shop inbox on this customer's chat.
      navigate("/shop/messages", { state: conversationId ? { openConversationId: String(conversationId) } : {} });
    } catch (e) {
      setError(e?.message || "Couldn't open a message");
    } finally {
      setMessagingId("");
    }
  }

  return (
    <div className="page-responsive max-w-5xl mx-auto min-h-screen">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Cart Leads 🛒</h1>
          <p className="text-sm text-gray-400">
            Buyers who opted in and have your item in their cart {thresholdDays}+ days.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-400">Show after</label>
          <input
            type="number"
            min={1}
            max={60}
            value={thresholdDays}
            disabled={savingThreshold}
            onChange={(e) => setThresholdDays(e.target.value)}
            onBlur={(e) => saveThreshold(e.target.value)}
            className="w-16 rounded-lg bg-gray-900/60 border border-gray-700 px-2 py-1.5 text-sm text-gray-100"
          />
          <span className="text-xs text-gray-400">days</span>
          <button
            onClick={load}
            className="ml-2 px-3 py-1.5 rounded-lg text-sm font-semibold bg-gray-700/50 text-gray-200 hover:bg-gray-700"
          >
            Refresh
          </button>
        </div>
      </div>

      {error ? (
        <div className="mb-4 rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-2 text-sm text-red-300">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="py-16 text-center text-gray-400">Loading…</div>
      ) : watchers.length === 0 ? (
        <div className="py-16 text-center text-gray-400">
          No cart leads yet. Buyers appear here once they opt in and leave your item in their cart {thresholdDays}+ days.
        </div>
      ) : (
        <div className="space-y-3">
          {watchers.map((w) => (
            <div
              key={w.userId}
              className="rounded-2xl border border-gray-700/60 bg-gray-800/40 p-4 flex flex-col sm:flex-row sm:items-center gap-4"
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {w.avatar ? (
                  <img src={toAbsUrl(w.avatar)} alt={w.name} className="w-11 h-11 rounded-full object-cover" />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-purple-500/20 flex items-center justify-center text-lg">🧑</div>
                )}
                <div className="min-w-0">
                  <a
                    href={`${USER_APP}/u/${w.userId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-bold text-gray-100 hover:text-purple-300 truncate block"
                  >
                    {w.name}
                  </a>
                  <div className="text-xs text-gray-400">
                    {w.itemCount} item{w.itemCount !== 1 ? "s" : ""} · in cart {w.daysSitting} day{w.daysSitting !== 1 ? "s" : ""}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex -space-x-2">
                  {(w.items || []).slice(0, 4).map((it, i) =>
                    it.image ? (
                      <img
                        key={i}
                        src={toAbsUrl(it.image)}
                        alt={it.title}
                        title={it.title}
                        className="w-9 h-9 rounded-lg object-cover border border-gray-700"
                      />
                    ) : null
                  )}
                </div>
                <button
                  onClick={() => messageBuyer(w)}
                  disabled={messagingId === w.userId}
                  className="px-4 py-2 rounded-lg text-sm font-bold bg-purple-600 text-white hover:bg-purple-500 disabled:opacity-60"
                >
                  {messagingId === w.userId ? "Opening…" : "💬 Message"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
